<template>
<ubi-page-container wide-main-content>
  <template v-slot:page-actions>
    <UsersEdit :userProp="{}" v-on:fetchList="fetchList()"></UsersEdit>
    <el-button
        v-if="showImport"
        type="warning"
        class="inline UsersList-import"
        @click="openImport"
    >{{ $t('users.import') }}</el-button>
    <input
        ref="importFile"
        type="file"
        accept=".csv,text/csv"
        class="UsersList-importFile"
        @change="onImportFile"
    />
  </template>
  <div v-loading="loading"
     :element-loading-text="$t('portals.list.chargementDonnees')">
  <el-form @submit.prevent.native="fetchList" label-position="top">
    <api-search-form
        ref="apiSearchForm"
        :key="$route.fullPath"
        route="users"
        business-domain="immo"
        :user-context="userContext"
        store-name="users"
        @search="handleSearchFromApi"
        @reset="handleResetFromApi"
        @ready="handleSearchFormReady"
    />
    <ubi-panel class="UsersList">
      <ubi-list-header
          slot="header"
          :limits="limits"
          :total="results.total"
          :page.sync="page"
          :limit.sync="itemsPerPage"
      >
        <template slot="center">
          <span v-if="showActiveQuota" class="UsersList-activeQuota">
            <span
                class="UsersList-activeQuotaCircle"
                :style="activeQuotaCircleStyle"
                aria-hidden="true"
            ></span>
            {{ $t('users.activeUsersQuota', { count: activeUsersCountLabel, quota: multiUserQuotaLabel }) }}
          </span>
        </template>
      </ubi-list-header>
      <ubi-list-selection
          slot="selection"
          v-model="selected"
          :current-page-ids="currentPageIds"
          @select="doSelect"
      ></ubi-list-selection>
      <template slot="actions">
        <el-button icon="export" size="mini" v-if="!selected.length" @click="handleExport">{{$t("users.exportAll")}}</el-button>
        <span v-else>
            <span class="inline">{{ $t("users.forSelection") }} ({{ selected.length }}) :</span>
            <el-button-group class="inline">
              <el-button class="UsersList-export" size="mini" @click="handleExport">{{$t("users.export")}}</el-button>
            </el-button-group>
          </span>
      </template>
      <ubi-list-footer
          slot="footer"
          v-if="results.total > 1"
          :limits="limits"
          :total="results.total"
          :page.sync="page"
          :limit.sync="itemsPerPage"
      ></ubi-list-footer>
      <el-table
          class="UsersList-table"
          :data="results.list"
          style="width: 100%"
      >
        <el-table-column width="55" align="center">
          <template v-slot:header>
            <span></span>
          </template>
          <template v-slot="{ row }">
            <el-checkbox
                :value="isSelected(row)"
                @change="toggleRow(row, $event)"
            />
          </template>
        </el-table-column>
        <el-table-column :label="$t('users.username')" min-width="160">
          <template v-slot="{ row }">
            {{ row.username }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('users.fullName')" min-width="180">
          <template v-slot="{ row }">
            {{ row.firstname | capitalize }} {{ row.lastname | capitalize }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('users.email')" min-width="220" prop="email"></el-table-column>
        <el-table-column :label="$t('users.role')" min-width="140">
          <template v-slot="{ row }">
            {{ $t(getFormattedRole(row)) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('users.columnStatus')" width="140" align="center">
          <template v-slot="{ row }">
            <el-switch
                :value="row.active"
                :disabled="!canToggleStatus(row)"
                active-color="#25a78d"
                @change="onStatusChange(row)"
            />
          </template>
        </el-table-column>
        <el-table-column :label="$t('users.columnActions')" min-width="260" align="left" header-align="left">
          <template v-slot="{ row }">
            <div class="UsersList-actions">
              <span class="UsersList-actionSlot">
                <el-tooltip :content="$t('users.edit')" placement="top" v-if="canEdit(row)">
                  <UsersEdit :userProp="row" :icon="true" v-on:fetchList="fetchList()" :key="'edit-' + row.id"></UsersEdit>
                </el-tooltip>
              </span>
              <span class="UsersList-actionSlot">
                <el-tooltip :content="$t('users.viewDetail')" placement="top">
                  <em class="el-icon-view UsersList-action" @click="openDetail(row)"></em>
                </el-tooltip>
              </span>
              <span class="UsersList-actionSlot">
                <el-tooltip :content="$t('users.delete')" placement="top" v-if="canDelete(row)">
                  <em class="el-icon-delete UsersList-action UsersList-action--danger" @click="deleteUser(row)"></em>
                </el-tooltip>
              </span>
              <span class="UsersList-actionSlot UsersList-actionSlot--switch">
                <button
                    type="button"
                    class="UsersList-switch"
                    :class="{ 'UsersList-switch--hidden': !canSwitchUser(row) }"
                    :aria-hidden="!canSwitchUser(row) ? 'true' : null"
                    :tabindex="canSwitchUser(row) ? null : -1"
                    @click="canSwitchUser(row) && switchUser(row.username)"
                >
                  {{ $t("users.logIn") }}
                </button>
              </span>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </ubi-panel>
  </el-form>
  <el-dialog
      :append-to-body="true"
      :visible.sync="detailVisible"
      :width="isNarrow ? '92%' : '720px'"
      custom-class="UsersList-detailDialog"
  >
    <template slot="title">
      <p class="UsersList-detailEyebrow">{{ $t('users.detailTitle') }}</p>
      <div class="UsersList-detailName">{{ detailDisplayName }}</div>
      <div class="UsersList-detailIdentity">
        <span class="UsersList-detailUsername">{{ displayValue(detailUser && detailUser.username) }}</span>
        <span
            v-if="detailUser"
            class="UsersList-detailStatus"
            :class="detailUser.active ? 'is-active' : 'is-inactive'"
        >{{ detailStatusLabel }}</span>
      </div>
    </template>
    <div v-if="detailUser" class="UsersList-detail">
      <p class="UsersList-detailSection">{{ $t('users.sectionAccount') }}</p>
      <el-row :gutter="16">
        <el-col :span="pairSpan">
          <div class="UsersList-detailField">
            <span class="UsersList-detailLabel">{{ $t('users.username') }}</span>
            <span class="UsersList-detailValue">{{ displayValue(detailUser.username) }}</span>
          </div>
        </el-col>
        <el-col :span="pairSpan">
          <div class="UsersList-detailField">
            <span class="UsersList-detailLabel">{{ $t('users.role') }}</span>
            <span class="UsersList-detailValue">{{ detailRoleLabel }}</span>
          </div>
        </el-col>
      </el-row>

      <p class="UsersList-detailSection">{{ $t('users.sectionIdentity') }}</p>
      <el-row :gutter="16">
        <el-col :span="pairSpan">
          <div class="UsersList-detailField">
            <span class="UsersList-detailLabel">{{ $t('users.firstname') }}</span>
            <span class="UsersList-detailValue">{{ displayValue(capitalizeValue(detailUser.firstname)) }}</span>
          </div>
        </el-col>
        <el-col :span="pairSpan">
          <div class="UsersList-detailField">
            <span class="UsersList-detailLabel">{{ $t('users.lastname') }}</span>
            <span class="UsersList-detailValue">{{ displayValue(capitalizeValue(detailUser.lastname)) }}</span>
          </div>
        </el-col>
      </el-row>
      <div class="UsersList-detailField">
        <span class="UsersList-detailLabel">{{ $t('users.email') }}</span>
        <a v-if="detailUser.email" class="UsersList-detailLink" :href="'mailto:' + detailUser.email">{{ detailUser.email }}</a>
        <span v-else class="UsersList-detailValue">—</span>
      </div>
      <el-row :gutter="16">
        <el-col :span="pairSpan">
          <div class="UsersList-detailField">
            <span class="UsersList-detailLabel">{{ $t('users.phone') }}</span>
            <span class="UsersList-detailValue">{{ displayValue(detailUser.phone) }}</span>
          </div>
        </el-col>
        <el-col :span="pairSpan">
          <div class="UsersList-detailField">
            <span class="UsersList-detailLabel">{{ $t('users.mobile') }}</span>
            <span class="UsersList-detailValue">{{ displayValue(detailUser.mobile) }}</span>
          </div>
        </el-col>
      </el-row>

      <p class="UsersList-detailSection">{{ $t('users.sectionContact') }}</p>
      <div class="UsersList-detailField">
        <span class="UsersList-detailLabel">{{ $t('users.address') }}</span>
        <span class="UsersList-detailValue">{{ displayValue(capitalizeValue(detailUser.address)) }}</span>
      </div>
      <el-row :gutter="16">
        <el-col :span="tripleSpan">
          <div class="UsersList-detailField">
            <span class="UsersList-detailLabel">{{ $t('users.postalCode') }}</span>
            <span class="UsersList-detailValue">{{ displayValue(detailUser.zipcode) }}</span>
          </div>
        </el-col>
        <el-col :span="tripleSpan">
          <div class="UsersList-detailField">
            <span class="UsersList-detailLabel">{{ $t('users.city') }}</span>
            <span class="UsersList-detailValue">{{ displayValue(capitalizeValue(detailUser.city)) }}</span>
          </div>
        </el-col>
        <el-col :span="tripleSpan">
          <div class="UsersList-detailField">
            <span class="UsersList-detailLabel">{{ $t('users.country') }}</span>
            <span class="UsersList-detailValue">{{ displayValue(capitalizeValue(detailUser.country)) }}</span>
          </div>
        </el-col>
      </el-row>
      <div class="UsersList-detailField">
        <span class="UsersList-detailLabel">{{ $t('users.price') }}</span>
        <a
            v-if="pricingHref(detailUser.pricing)"
            class="UsersList-detailLink"
            :href="pricingHref(detailUser.pricing)"
            target="_blank"
            rel="noopener noreferrer"
        >{{ detailUser.pricing }}</a>
        <span v-else class="UsersList-detailValue">{{ displayValue(detailUser.pricing) }}</span>
      </div>
    </div>
    <span class="dialog-footer" slot="footer">
      <el-button @click="detailVisible = false">{{ $t('users.close') }}</el-button>
    </span>
  </el-dialog>
  </div>
</ubi-page-container>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import { getUsers, updateUser, exportUsers, deleteUserContact, importUsers } from '@/services/users'
import ElButton from '../../components/Button'
import { getSwitchedToken } from '@/services/auth'
import UsersEdit from './UsersEdit'
import ApiSearchForm from '@/components/search/ApiSearchForm.vue'
import { convertApiFiltersToStore } from '@/utils/filterConverter'
import UbiPageContainer from '@/components/PageContainer.vue'
import UbiPanel from '@/components/Panel.vue'
import UbiListHeader from '@/components/lists/ListHeader.vue'
import UbiListSelection from '@/components/lists/ListSelection.vue'
import UbiListFooter from '@/components/lists/ListFooter.vue'
const jwtDecode = require('jwt-decode')

export default {
  name: 'UsersList',
  inject: ['browserContext'],
  components: {
    UbiListFooter,
    UbiListSelection,
    UbiListHeader,
    UbiPanel,
    UbiPageContainer,
    UsersEdit,
    ElButton,
    ApiSearchForm
  },
  data () {
    return {
      loading: false,
      limits: [10, 20, 50],
      selected: [],
      detailVisible: false,
      detailUser: null,
      activeUsersCount: null
    }
  },
  computed: {
    ...mapState('users', ['filters', 'results']),
    ...mapGetters('users', ['getFormattedRole']),
    ...mapGetters(['isMainAccount', 'isSubAdmin', 'isSubUser', 'hasMultiUserAdmin']),
    showImport () {
      return !this.isSubUser && this.hasMultiUserAdmin
    },
    showActiveQuota () {
      return this.multiUserQuota > 0
    },
    userContext () {
      return {
        userId: this.sitimmoUser.id,
        metier: this.sitimmoUser.metier,
        accountType: this.isMainAccount ? 'main' : 'secondary'
      }
    },
    sitimmoUser () {
      return this.$store.getters.getSitimmoUser
    },
    connectedUser () {
      return this.$store.getters.getUser
    },
    currentPageIds () {
      return (this.results.list || []).map(function (row) {
        return row.id
      })
    },
    isNarrow () {
      return this.browserContext && this.browserContext.down('md')
    },
    pairSpan () {
      return this.isNarrow ? 24 : 12
    },
    tripleSpan () {
      return this.isNarrow ? 24 : 8
    },
    detailDisplayName () {
      const user = this.detailUser
      if (!user) {
        return ''
      }
      const name = [this.capitalizeValue(user.firstname), this.capitalizeValue(user.lastname)].filter(function (part) {
        return part
      }).join(' ')
      return name || this.displayValue(user.username)
    },
    detailStatusLabel () {
      if (!this.detailUser) {
        return ''
      }
      return this.detailUser.active ? this.$t('users.enabled') : this.$t('users.disabled')
    },
    detailRoleLabel () {
      if (!this.detailUser) {
        return '—'
      }
      return this.$t(this.getFormattedRole(this.detailUser))
    },
    page: {
      get () {
        return this.filters.page
      },
      set (value) {
        this.setPage(value)
        this.fetchList()
      }
    },
    itemsPerPage: {
      get () {
        return this.filters.itemsPerPage
      },
      set (value) {
        this.setItemsPerPage(value)
        this.fetchList()
      }
    },
    decodedToken () {
      return jwtDecode(this.$store.getters.getToken)
    },
    multiUserQuota () {
      const getter = this.$store.getters.getActiveDataByCode
      if (typeof getter !== 'function') {
        return null
      }
      const data = getter('MULTI_USER_QUOTA')
      if (!data || data.value == null || data.value === '') {
        return null
      }
      const quota = Number(data.value)
      if (isNaN(quota)) {
        return null
      }
      return quota
    },
    activeUsersCountLabel () {
      if (this.activeUsersCount == null) {
        return '—'
      }
      return this.activeUsersCount
    },
    multiUserQuotaLabel () {
      if (this.multiUserQuota == null) {
        return '—'
      }
      return this.multiUserQuota
    },
    activeQuotaFill () {
      const quota = this.multiUserQuota
      const count = this.activeUsersCount
      if (!quota || quota <= 0 || !count || count <= 0) {
        return 0
      }
      const ratio = count / quota
      const capped = ratio > 1 ? 1 : ratio
      return Math.round(capped * 360)
    },
    activeQuotaCircleStyle () {
      return { '--quota-fill': this.activeQuotaFill + 'deg' }
    }
  },
  filters: {
    capitalize: function (value) {
      if (!value) return ''
      value = value.toString()
      return value.charAt(0).toUpperCase() + value.slice(1)
    }
  },
  methods: {
    ...mapMutations('users', [
      'setPage',
      'addFilter',
      'setItemsPerPage',
      'setResults',
      'resetFilters'
    ]),
    async fetchList () {
      this.loading = true
      const results = await Promise.all([
        getUsers(this.filters),
        getUsers({ active: true, itemsPerPage: 1, page: 1 })
      ])
      const utilisateur = results[0]
      const activeUsers = results[1]
      if (activeUsers && activeUsers.type !== 'error') {
        this.activeUsersCount = activeUsers.total
      }
      if (utilisateur.type === 'error') {
        this.loading = false
        return false
      }
      this.setResults(utilisateur)
      this.loading = false
    },
    normalizeUsersApiFilters (converted) {
      const out = { ...converted }
      if (Object.prototype.hasOwnProperty.call(out, 'active')) {
        const a = out.active
        if (a === '' || a === null || a === undefined) {
          out.active = undefined
        } else if (a === 'true' || a === true) {
          out.active = true
        } else if (a === 'false' || a === false) {
          out.active = false
        }
      }
      return out
    },
    handleSearchFromApi (filters) {
      let converted = convertApiFiltersToStore(filters, {
        metier: this.sitimmoUser.metier
      })
      converted = this.normalizeUsersApiFilters(converted)
      this.addFilter(converted)
      this.fetchList()
    },
    handleResetFromApi () {
      this.resetFilters()
      this.fetchList()
    },
    handleSearchFormReady () {
    },
    isSelected (row) {
      return this.selected.indexOf(row.id) !== -1
    },
    toggleRow (row, checked) {
      if (checked) {
        if (this.selected.indexOf(row.id) === -1) {
          this.selected = this.selected.concat([row.id])
        }
        return
      }
      this.selected = this.selected.filter(function (id) {
        return id !== row.id
      })
    },
    canToggleStatus (row) {
      if (!row) {
        return false
      }
      const notSubAdmin = row.roles && !row.roles.includes('ROLE_SUB_ADMIN')
      const isContact = row.roles && row.roles.includes('ROLE_CONTACT')
      return this.connectedUser.username !== row.username
        && !row.mainAccount
        && (this.isMainAccount || (this.isSubAdmin && notSubAdmin) || isContact)
    },
    canEdit (row) {
      if (!row || !row.id) {
        return false
      }
      return this.isMainAccount
        || (this.isSubAdmin && !row.mainAccount && (row.roles && !row.roles.includes('ROLE_SUB_ADMIN')))
        || row.username === this.connectedUser.username
        || (row.roles && row.roles.includes('ROLE_CONTACT'))
    },
    canDelete (row) {
      if (!row || !row.roles || !this.connectedUser.roles || row.id === this.connectedUser.id) {
        return false
      }
      const actorIsAdmin = this.connectedUser.roles.includes('ROLE_ADMIN') || this.connectedUser.roles.includes('ROLE_SUB_ADMIN')
      const targetIsDeletable = row.roles.includes('ROLE_SUB_USER') || row.roles.includes('ROLE_CONTACT')
      return !!(this.connectedUser.mainAccount || (actorIsAdmin && targetIsDeletable))
    },
    canSwitchUser (row) {
      if (!row || !row.roles || !row.active || row.username === this.connectedUser.username) {
        return false
      }
      if (row.roles.includes('ROLE_CONTACT')) {
        return false
      }
      const subAdminOnUser = this.isSubAdmin && !row.mainAccount && !row.roles.includes('ROLE_SUB_ADMIN')
      const switchedFrom = this.decodedToken && row.username === this.decodedToken.switchedFromUser
      return !!(this.isMainAccount || subAdminOnUser || switchedFrom)
    },
    openDetail (row) {
      this.detailUser = row
      this.detailVisible = true
    },
    onStatusChange (row) {
      if (!this.canToggleStatus(row)) {
        return
      }
      this.eventChangeUserState(row.active)
      this.onSwitchPublished(row)
    },
    async doSelect (type) {
      switch (type) {
        case '*':
          this.selected = this.results.list.map(({ id }) => id)
          break
      }
    },
    upperCase (string) {
      return string.charAt(0).toUpperCase() + string.substr(1)
    },
    eventChangeUserState (state) {
      if (this.$ga && state == true) {
        this.$ga.event('mes_comptes', 'clic', 'desactiver', this.sitimmoUser.id)
      }
      if (this.$ga && state == false) {
        this.$ga.event('mes_comptes', 'clic', 'activer', this.sitimmoUser.id)
      }
    },
    openImport () {
      if (this.$refs.importFile) {
        this.$refs.importFile.click()
      }
    },
    async onImportFile (event) {
      const files = event.target && event.target.files
      const file = files && files[0]
      if (event.target) {
        event.target.value = ''
      }
      if (!file) {
        return
      }
      this.loading = true
      const result = await importUsers(file)
      this.loading = false
      if (!result || result.type === 'error') {
        this.$message({ message: this.$t('users.importError'), type: 'error' })
        return
      }
      const rejected = result.rejected || []
      const summary = this.$t('users.importSuccess', {
        created: result.created || 0,
        updated: result.updated || 0
      })
      if (rejected.length) {
        const details = rejected.map((item) => {
          if (item.line) {
            return this.$t('users.importRejectedLine', { line: item.line, reason: item.reason })
          }
          return item.reason
        }).join(' | ')
        this.$message({ message: summary + ' ' + details, type: 'warning' })
      } else {
        this.$message({ message: summary, type: 'success' })
      }
      if (result.created || result.updated) {
        this.fetchList()
      }
    },
    async handleExport () {
      if (this.$ga) {
        this.$ga.event('mes_comptes', 'clic', 'exporter', this.sitimmoUser.id)
      }
      this.exportLoading = true
      const ids = this.selected.length ? this.selected : undefined
      const response = await exportUsers(ids)
      this.exportLoading = false
      if (response.type === 'error') {
        this.loading = false
        return false
      }
    },
    async switchUser (username) {
      this.$confirm(this.$t('users.confirmationMessage'), this.$t('users.caution'))
        .then(async () => {
          const switchedToken = await getSwitchedToken(username)
          await this.$router.push({ name: 'login', params: { token: switchedToken } })
        }).catch(() => {
        })
    },
    async onSwitchPublished (user) {
      this.loading = true
      const response = await updateUser({ id: user.id, active: !user.active })
      if (response.type === 'error') {
        this.$message({ message: this.$t('error.found'), type: 'error' })
        this.loading = false
        return false
      }
      await this.fetchList()
    },
    capitalizeValue (value) {
      const capitalize = this.$options.filters.capitalize
      return capitalize(value)
    },
    displayValue (value) {
      if (value == null || String(value).trim() === '') {
        return '—'
      }
      return String(value)
    },
    pricingHref (value) {
      if (value == null || String(value).trim() === '') {
        return ''
      }
      const text = String(value).trim()
      if (/^https?:\/\//i.test(text)) {
        return text
      }
      return ''
    },
    deleteUser (user) {
      return this.$confirm(this.$t('users.deleteConfirm'), this.$t('users.caution'))
        .then(async () => {
          this.loading = true
          await deleteUserContact(user.id)
          this.$message({ message: this.$t('users.messageDelete'), type: 'success' })
          await this.fetchList()
        }).catch(() => {
        })
    }
  }
}
</script>

<style lang="scss">
	@import "@/theme/_bootstrap.scss";

  .UsersList-import {
    margin-left: map-get($spacers, 2);
  }

  .UsersList-importFile {
    display: none;
  }

  .UsersList-activeQuota {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    font-size: $font-size-base * 1.6;
  }

  .UsersList-activeQuotaCircle {
    width: 25.6px;
    height: 25.6px;
    margin-right: 12.8px;
    border-radius: 50%;
    flex: 0 0 25.6px;
    background: conic-gradient($green var(--quota-fill, 0deg), #d7e0e2 0);
    mask: radial-gradient(farthest-side, transparent 58%, #000 60%);
  }

  .UsersList-actions {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-wrap: nowrap;
    width: 100%;
  }

  .UsersList-actionSlot {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    flex: 0 0 16px;
    width: 16px;
    min-width: 16px;
    max-width: 16px;
    height: 28px;
  }

  .UsersList-actionSlot + .UsersList-actionSlot {
    margin-left: 10px;
  }

  .UsersList-actionSlot + .UsersList-actionSlot--switch {
    margin-left: 28px;
  }

  .UsersList-actionSlot--switch {
    flex: 0 0 auto;
    width: auto;
    min-width: 0;
    max-width: none;
  }

  .UsersList-action {
    cursor: pointer;
    font-size: 16px;
    line-height: 1;

    &--danger {
      color: $red;
    }
  }

  .UsersList-switch {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: auto;
    height: 32px;
    margin: 0;
    padding: 0 12px;
    border-radius: 6px;
    border: 1px solid $blue;
    background-color: $blue;
    color: #ffffff;
    font-family: $font-family-sans-serif-alt;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    cursor: pointer;
    box-sizing: border-box;
    transition: background-color 150ms ease, border-color 150ms ease;

    &:hover {
      background-color: mix(#fff, $blue, 20%);
      border-color: mix(#fff, $blue, 20%);
      color: #ffffff;
    }
  }

  .UsersList-switch--hidden {
    visibility: hidden;
    pointer-events: none;
  }

  .UsersList-detailEyebrow {
    margin: 0 0 map-get($spacers, 2);
    color: $black-variant;
    font-family: $font-family-sans-serif-alt;
    font-size: $font-size-xs;
    font-weight: $font-weight-bold;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .UsersList-detailName {
    color: $black;
    font-family: $font-family-sans-serif-alt;
    font-size: $h2-font-size;
    font-weight: $font-weight-bold;
    line-height: 1.3;
  }

  .UsersList-detailIdentity {
    display: flex;
    align-items: center;
    margin-top: map-get($spacers, 1);
  }

  .UsersList-detailUsername {
    color: $black-variant;
    font-size: $font-size-sm;
  }

  .UsersList-detailStatus {
    display: inline-flex;
    align-items: center;
    margin-left: map-get($spacers, 2);
    padding: 2px map-get($spacers, 2);
    border-radius: 10px;
    font-size: $font-size-xs;

    &::before {
      content: '';
      width: 6px;
      height: 6px;
      margin-right: 6px;
      border-radius: 50%;
      background: currentColor;
    }

    &.is-active {
      color: $green;
      background: $blue-pastel;
    }

    &.is-inactive {
      color: $text-muted;
      background: $body-bg;
    }
  }

  .UsersList-detailSection {
    margin: map-get($spacers, 3) 0 map-get($spacers, 2);
    color: $black-variant;
    font-family: $font-family-sans-serif-alt;
    font-size: $font-size-xs;
    font-weight: $font-weight-bold;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .UsersList-detailField {
    margin-bottom: map-get($spacers, 3);
  }

  .UsersList-detailLabel {
    display: block;
    margin-bottom: map-get($spacers, 1);
    color: $black-variant;
    font-size: $font-size-sm;
  }

  .UsersList-detailValue {
    display: block;
    color: $black;
  }

  .UsersList-detailLink {
    color: $red;
    text-decoration: underline;
  }
</style>
